/**
 * LeagueNewspaper — The Hardwood Herald (v2 redesign)
 *
 * Weekly AI-written digest of your league, set like a broadsheet:
 * newsprint card (#FDFCF8), Georgia serif, double-rule masthead,
 * drop-cap lead story, "Around the league" sidebar, score box.
 *
 * The ONLY surface in the app where serif type is allowed.
 */

import { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { leaguesApi } from '../services/api'
import AppShell from '../components/AppShell'
import { JAI_FONTS } from '../components/JaiBrand'
import { V2, V2Page } from '../components/JaiV2'
import { JAIMascot } from '../components/JAIMascot'

const SERIF = "Georgia, 'Times New Roman', serif"

// ── Preview flag ───────────────────────────────────────────────────────────────
const IS_PREVIEW =
  import.meta.env.DEV &&
  new URLSearchParams(window.location.search).get('preview') === '1'

// ── Mock data ──────────────────────────────────────────────────────────────────
const PREVIEW_LEAGUE = {
  id: 'preview',
  name: 'The Hardwood Cartel',
  teamName: 'Rim Reapers',
  season: 2025,
}

const PREVIEW_EDITION = {
  week: 14,
  date: 'Week of May 12, 2026',
  headline: 'Rim Reapers Roll to Fifth Straight — Rivals Left Speechless',
  subheadline: 'A career night from Jayson Tatum and a late Curry surge sealed the deal in a nervy 184–171 finish.',
  matchReport: `The Hardwood Cartel faithful had their hearts in their mouths as Dunks & Dimes surged to within 9 points midway through Wednesday, only for Jayson Tatum to put the contest to bed with a monster 31/9/4 performance that will be talked about in this league for weeks.\n\nRim Reapers managed the week expertly — JAI's pre-week advice to start Tatum over Bam Adebayo proved prescient, contributing an estimated 18 extra fantasy points over a side that was forced to chase all week.\n\nThe win extends Rim Reapers' unbeaten run to five matches and opens a 3-game gap at the top of the standings with six weeks remaining.`,
  scoreOfTheWeek: { score: '184.6 — 171.2', note: 'Rim Reapers def. Dunks & Dimes' },
  powerMove: {
    player: 'Jayson Tatum',
    pts: 62.0,
    desc: "Started over Bam Adebayo on JAI's recommendation. Tatum delivered 31 pts, 9 reb, 4 ast and 2 blk — the second-highest single-game fantasy total in this league this season.",
  },
  blunder: {
    manager: 'Dunks & Dimes',
    regret: '72.5 pts left on the bench',
    desc: 'Left Kevin Durant on the bench — KD dropped 41 pts, 7 reb and 3 blk in a blowout win, contributing nothing to a lineup that desperately needed the output.',
  },
  headlines: [
    'Nikola Jokić on pace for a 4th consecutive triple-double week — owners celebrating.',
    'Stephen Curry listed as questionable for Friday — JAI flagging this risk for Rim Reapers.',
    'Waiver wire explosion: Alperen Şengün has 3 straight 50+ fantasy point games and is 47% owned.',
    'Anthony Edwards owners panicking after a quiet 19-point, zero-assist showing.',
    'Three trades completed this week — the Anunoby for Mitchell swap is drawing heavy debate.',
  ],
  standings: [
    { rank: 1, name: 'Rim Reapers',     record: '10-4', pts: 1842.5, trend: 'up' },
    { rank: 2, name: 'Hoop Schemers',   record: '7-7',  pts: 1790.2, trend: 'same' },
    { rank: 3, name: 'Dunks & Dimes',   record: '7-7',  pts: 1765.8, trend: 'down' },
    { rank: 4, name: 'Playoff or Bust', record: '6-8',  pts: 1710.1, trend: 'down' },
    { rank: 5, name: 'The Benchmarks',  record: '5-9',  pts: 1688.4, trend: 'up' },
  ],
}

function TrendIcon({ trend }) {
  if (trend === 'up')   return <span style={{ color: V2.success, fontSize: 12 }}>▲</span>
  if (trend === 'down') return <span style={{ color: V2.error, fontSize: 12 }}>▼</span>
  return <span style={{ color: 'rgba(10,31,68,0.3)', fontSize: 12 }}>–</span>
}

// Mono section label inside the paper
function PaperLabel({ children, style = {} }) {
  return (
    <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: V2.navy, fontFamily: JAI_FONTS.mono, borderBottom: '1px solid rgba(10,31,68,0.2)', paddingBottom: 8, marginBottom: 12, ...style }}>
      {children}
    </div>
  )
}

// ── Main component ─────────────────────────────────────────────────────────────

export default function LeagueNewspaper() {
  const { id } = useParams()
  const [league, setLeague] = useState(IS_PREVIEW ? PREVIEW_LEAGUE : null)
  const [edition] = useState(PREVIEW_EDITION)
  const [loading, setLoading] = useState(!IS_PREVIEW)

  useEffect(() => {
    if (IS_PREVIEW) return
    leaguesApi.get(id).then(r => {
      setLeague(r.data.league)
      setLoading(false)
    }).catch(() => setLoading(false))
  }, [id])

  if (loading) {
    return (
      <AppShell>
        <div className="flex flex-col items-center justify-center h-full min-h-screen gap-3">
          <JAIMascot size={56} state="thinking" animate={true} />
          <span className="text-sm" style={{ color: V2.faint }}>Loading…</span>
        </div>
      </AppShell>
    )
  }

  const lead = edition.matchReport.split('\n\n')

  return (
    <AppShell>
      <V2Page
        kicker={
          <Link to={`/league/${id}`} style={{ color: 'inherit', textDecoration: 'none' }}>
            {league?.name ? `${league.name} · Week ${edition.week} edition` : `Week ${edition.week} edition`}
          </Link>
        }
        title="Hardwood Herald"
        sub="Your league's AI-written weekly — every matchup, every storyline."
        maxWidth={820}
      >
        {/* ── The paper ─────────────────────────────────────────────────── */}
        <div style={{ background: '#FDFCF8', border: '1px solid rgba(10,31,68,0.14)', borderRadius: 4, padding: '28px 36px', boxShadow: '0 2px 12px rgba(10,31,68,0.07)' }}>

          {/* Masthead */}
          <div style={{ textAlign: 'center', borderBottom: '3px double rgba(10,31,68,0.6)', paddingBottom: 14, marginBottom: 4 }}>
            <div style={{ fontFamily: SERIF, fontSize: 34, fontWeight: 700, color: V2.navy, letterSpacing: '0.01em' }}>
              The Hardwood Herald
            </div>
            <div style={{ fontSize: 10.5, letterSpacing: '0.2em', textTransform: 'uppercase', color: V2.mute, fontFamily: JAI_FONTS.mono, marginTop: 6 }}>
              {league?.name || 'Your league'} · {edition.date} · No. {edition.week} · Written by JAI
            </div>
          </div>
          <div style={{ borderBottom: '1px solid rgba(10,31,68,0.25)', marginBottom: 20 }} />

          {/* Lead story + sidebar */}
          <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr', gap: 26 }}>
            <div>
              <h2 style={{ fontFamily: SERIF, fontSize: 27, fontWeight: 700, color: V2.navy, lineHeight: 1.15, margin: '0 0 8px', fontStyle: 'italic' }}>
                {edition.headline}
              </h2>
              <div style={{ fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase', color: V2.mute, fontFamily: JAI_FONTS.mono, marginBottom: 12 }}>
                By JAI · League Correspondent
              </div>
              <p style={{ fontFamily: SERIF, fontStyle: 'italic', fontSize: 14.5, lineHeight: 1.6, color: 'rgba(10,31,68,0.6)', margin: '0 0 14px' }}>
                {edition.subheadline}
              </p>
              {lead.map((para, i) => (
                <p key={i} style={{ fontFamily: SERIF, fontSize: 14.5, lineHeight: 1.7, color: 'rgba(10,31,68,0.85)', margin: i < lead.length - 1 ? '0 0 12px' : 0 }}>
                  {i === 0 && (
                    <span style={{ float: 'left', fontSize: 42, lineHeight: '34px', paddingRight: 8, fontWeight: 700, color: V2.navy, fontFamily: SERIF }}>
                      {para.charAt(0)}
                    </span>
                  )}
                  {i === 0 ? para.slice(1) : para}
                </p>
              ))}
            </div>

            {/* Sidebar column */}
            <div style={{ borderLeft: '1px solid rgba(10,31,68,0.2)', paddingLeft: 22 }}>
              <PaperLabel>Around the league</PaperLabel>
              {edition.headlines.slice(0, 4).map((text, i) => (
                <div key={i} style={{ marginBottom: 13 }}>
                  <div style={{ fontFamily: SERIF, fontSize: 12.5, lineHeight: 1.55, color: 'rgba(10,31,68,0.75)' }}>{text}</div>
                </div>
              ))}
              <div style={{ border: '1px solid rgba(10,31,68,0.12)', padding: '10px 13px', marginTop: 4 }}>
                <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.12em', color: V2.mute, fontFamily: JAI_FONTS.mono, marginBottom: 6 }}>
                  SCORE OF THE WEEK
                </div>
                <div style={{ fontFamily: JAI_FONTS.mono, fontSize: 16, fontWeight: 800, color: V2.navy }}>
                  {edition.scoreOfTheWeek.score}
                </div>
                <div style={{ fontSize: 11, color: V2.mute, marginTop: 2, fontFamily: SERIF, fontStyle: 'italic' }}>
                  {edition.scoreOfTheWeek.note}
                </div>
              </div>
            </div>
          </div>

          <div style={{ borderTop: '1px solid rgba(10,31,68,0.25)', margin: '24px 0 20px' }} />

          {/* Power move + blunder columns */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 26 }}>
            <div>
              <PaperLabel>Power move of the week</PaperLabel>
              <div style={{ fontFamily: SERIF, fontWeight: 700, fontSize: 16, color: V2.navy, marginBottom: 2 }}>{edition.powerMove.player}</div>
              <div style={{ fontFamily: JAI_FONTS.mono, fontSize: 18, fontWeight: 800, color: V2.blue, marginBottom: 8 }}>{edition.powerMove.pts.toFixed(1)} fpts</div>
              <p style={{ fontFamily: SERIF, fontSize: 12.5, lineHeight: 1.6, color: 'rgba(10,31,68,0.75)', margin: 0 }}>{edition.powerMove.desc}</p>
            </div>
            <div>
              <PaperLabel>Blunder of the week</PaperLabel>
              <div style={{ fontFamily: SERIF, fontWeight: 700, fontSize: 16, color: V2.navy, marginBottom: 2 }}>{edition.blunder.manager}</div>
              <div style={{ fontFamily: JAI_FONTS.mono, fontSize: 13, fontWeight: 800, color: V2.error, marginBottom: 8 }}>{edition.blunder.regret}</div>
              <p style={{ fontFamily: SERIF, fontSize: 12.5, lineHeight: 1.6, color: 'rgba(10,31,68,0.75)', margin: 0 }}>{edition.blunder.desc}</p>
            </div>
          </div>

          <div style={{ borderTop: '1px solid rgba(10,31,68,0.25)', margin: '24px 0 20px' }} />

          {/* Standings snapshot */}
          <PaperLabel>Standings snapshot</PaperLabel>
          <div>
            {edition.standings.map((team, i) => {
              const isMe = team.name === league?.teamName
              return (
                <div key={team.rank} style={{
                  display: 'flex', alignItems: 'center', gap: 14, padding: '7px 4px',
                  borderBottom: i < edition.standings.length - 1 ? '1px solid rgba(10,31,68,0.08)' : 'none',
                  boxShadow: isMe ? 'inset 3px 0 0 #1E5BD6' : 'none',
                }}>
                  <span style={{ width: 26, fontSize: 12, fontWeight: 800, fontFamily: JAI_FONTS.mono, color: isMe ? V2.blue : V2.faint }}>
                    {String(team.rank).padStart(2, '0')}
                  </span>
                  <span style={{ flex: 1, fontFamily: SERIF, fontSize: 13.5, fontWeight: isMe ? 700 : 400, color: V2.navy }}>{team.name}</span>
                  <span style={{ fontSize: 12, color: V2.mute, fontFamily: JAI_FONTS.mono }}>{team.record}</span>
                  <span style={{ fontSize: 12, color: V2.navy, fontFamily: JAI_FONTS.mono, minWidth: 58, textAlign: 'right', fontWeight: 600 }}>{team.pts}</span>
                  <TrendIcon trend={team.trend} />
                </div>
              )
            })}
          </div>

          {/* Colophon */}
          <div style={{ marginTop: 22, paddingTop: 12, borderTop: '1px solid rgba(10,31,68,0.25)', textAlign: 'center' }}>
            <span style={{ fontSize: 10.5, letterSpacing: '0.16em', textTransform: 'uppercase', color: V2.faint, fontFamily: JAI_FONTS.mono }}>
              Generated by JAI · Publishes each Monday · Week {edition.week}
            </span>
          </div>
        </div>
      </V2Page>
    </AppShell>
  )
}
